import React from 'react';
import { useTranslation } from 'react-i18next';
import type { NiuNiuRoundHistory, NiuNiuWinnerRoundHistory, CardString } from '../types/niuNiu';
import './NiuNiuRoundDetails.css';

interface NiuNiuRoundDetailsProps {
  data: string; // JSON string
  gameId: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Английские подписи комбинаций — так ответы хранятся и сверяются (в том числе сервером в дуэлях).
// Показывать их пользователю нужно через localizeNiuNiuLabel.
const handTypeLabels: { [key: string]: string } = {
  'no_game': 'No Game',
  'niu_1': 'Niu 1',
  'niu_2': 'Niu 2',
  'niu_3': 'Niu 3',
  'niu_4': 'Niu 4',
  'niu_5': 'Niu 5',
  'niu_6': 'Niu 6',
  'niu_7': 'Niu 7',
  'niu_8': 'Niu 8',
  'niu_9': 'Niu 9',
  'niu_niu': 'Niu Niu',
};

export const formatHandType = (handType: string): string => {
  return handTypeLabels[handType] || handType;
};

// Ответ в «служебном» виде (английские подписи): уходит на сервер и в результаты, не переводить
export const formatNiuNiuAnswer = (answer: any, mode: 'points' | 'winner'): string => {
  if (answer === null || answer === undefined || answer === '') {
    return '-';
  }

  if (mode === 'points') {
    // For points mode, answer is HandType
    return formatHandType(String(answer));
  } else {
    // For winner mode, answer can be single number or array of numbers
    if (Array.isArray(answer)) {
      if (answer.length === 0) return '-';
      if (answer.length === 1) return `Hand #${answer[0] + 1}`;
      return answer.map(idx => `Hand #${idx + 1}`).join(', ');
    }
    return `Hand #${Number(answer) + 1}`;
  }
};

// Перевод служебной подписи для показа: «Niu 5» → «Ню 5», «No Game» → «Старшая карта», «Hand #2» → «Рука 2».
// t — с пространством имён gameplay.
export const localizeNiuNiuLabel = (label: string, t: Translate): string => {
  return label
    .split(', ')
    .map((part) => {
      const hand = part.match(/^Hand #(\d+)$/);
      if (hand) return t('niuNiu.details.hand', { n: Number(hand[1]) });
      if (part === 'No Game') return t('niuNiu.handTypes.noGame');
      if (part === 'Niu Niu') return t('niuNiu.handTypes.niuNiu');
      const niu = part.match(/^Niu (\d)$/);
      if (niu) return t(`niuNiu.handTypes.niu${niu[1]}`);
      return part;
    })
    .join(', ');
};

const getCardImagePath = (card: CardString): string => {
  const rank = card.slice(0, -1);
  const suit = card.slice(-1);
  return `/img/cards/${rank}${suit.toLowerCase()}.webp`;
};

export const NiuNiuRoundDetails: React.FC<NiuNiuRoundDetailsProps> = ({ data, gameId }) => {
  const { t } = useTranslation('gameplay');
  const typeLabel = (type: string) => localizeNiuNiuLabel(formatHandType(type), t);

  try {
    const roundData: NiuNiuRoundHistory | NiuNiuWinnerRoundHistory = JSON.parse(data);

    if (gameId === 'niu-niu-points') {
      // Points mode - single hand
      const pointsData = roundData as NiuNiuRoundHistory;
      const userAnswerLabel = pointsData.userAnswer ? typeLabel(pointsData.userAnswer) : t('niuNiu.details.noAnswer');
      const correctAnswerLabel = typeLabel(pointsData.correctAnswer);

      return (
        <div className="niu-niu-round-details">
          <h3>{t('niuNiu.details.round', { n: pointsData.roundNumber })}</h3>

          <div className="round-info">
            <div className="info-row">
              <span className="label">{t('niuNiu.details.yourAnswer')}:</span>
              <span className={`value ${pointsData.isCorrect ? 'correct' : 'incorrect'}`}>
                {userAnswerLabel}
              </span>
            </div>
            <div className="info-row">
              <span className="label">{t('niuNiu.details.correctAnswer')}:</span>
              <span className="value">{correctAnswerLabel}</span>
            </div>
          </div>

          <div className="cards-section">
            <h4>{t('niuNiu.details.cards')}</h4>
            <div className="cards-container">
              {pointsData.cards.map((card, idx) => (
                <img
                  key={idx}
                  src={getCardImagePath(card)}
                  alt={card}
                  className="card-image"
                />
              ))}
            </div>
          </div>
        </div>
      );
    } else {
      // Winner mode - single winner only
      const winnerData = roundData as NiuNiuWinnerRoundHistory;
      const userChoice = winnerData.userChoice;
      const correctChoice = winnerData.correctChoice;

      const formatChoice = (choice: number | null) => {
        if (choice === null) return t('niuNiu.details.noAnswer');
        return t('niuNiu.details.hand', { n: choice + 1 });
      };

      return (
        <div className="niu-niu-round-details">
          <h3>{t('niuNiu.details.round', { n: winnerData.roundNumber })}</h3>

          <div className="round-info">
            <div className="info-row">
              <span className="label">{t('niuNiu.details.yourChoice')}:</span>
              <span className={`value ${winnerData.isCorrect ? 'correct' : 'incorrect'}`}>
                {formatChoice(userChoice)}
              </span>
            </div>
            <div className="info-row">
              <span className="label">{t('niuNiu.details.correctAnswer')}:</span>
              <span className="value">{formatChoice(correctChoice)}</span>
            </div>
          </div>

          <div className="hands-section">
            <h4>{t('niuNiu.details.allHands')}</h4>
            {winnerData.hands.map((hand, idx) => {
              const isWinner = correctChoice === idx;
              const isUserChoice = userChoice === idx;
              const isCorrectChoice = isWinner && isUserChoice;

              return (
                <div
                  key={idx}
                  className={`hand-container ${isWinner ? 'winning-hand' : ''} ${isUserChoice ? 'selected-hand' : ''}`}
                >
                  <div className="hand-header">
                    <span className="hand-number">{t('niuNiu.details.hand', { n: idx + 1 })}</span>
                    {isWinner && <span className="badge winning">{t('niuNiu.details.winner')}</span>}
                    {isUserChoice && !isCorrectChoice && <span className="badge selected">{t('niuNiu.details.yourChoice')}</span>}
                    {isCorrectChoice && <span className="badge correct">{t('niuNiu.details.yourChoiceCorrect')}</span>}
                  </div>
                  <div className="cards-container">
                    {hand.cards.map((card, cardIdx) => (
                      <img
                        key={cardIdx}
                        src={getCardImagePath(card)}
                        alt={card}
                        className="card-image"
                      />
                    ))}
                  </div>
                  <div className="hand-info">
                    <span>{typeLabel(hand.result.type)}</span>
                    <span>{t('niuNiu.details.points', { n: hand.result.points })}</span>
                    {hand.result.isUltimate && <span style={{ color: '#f39c12' }}>{t('niuNiu.details.ultimate')}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  } catch {
    return (
      <div className="niu-niu-round-details error">
        <p>{t('niuNiu.details.error')}</p>
      </div>
    );
  }
};
